import { FileDownloadOutlined, RepeatSharp } from "@mui/icons-material";
import { Box, IconButton, SvgIconTypeMap, Typography } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useRef, useState } from "react";
import dayjs from "dayjs";

import emitSnackbar from "../../../../emitSnackbar";
import DragDrop from "../../Layout/DragDrop";
import DragDropParent from "../../Layout/DragDropParent";
import { Study } from "../../models";
import { FileEditState, FileType } from "../manageStudyModels";
import { EditStudyContext } from "../useEditStudyContext";
import useStudyFileStore from "../../useStudyFileStore";
import FileProgressSpinner from "./FileProgressSpinner";
import { AxiosProgressEvent } from "axios";
import useFeatureFlag from "../../../../featureFlags/useFeatureFlag";
import { Feature } from "../../../../featureFlags/featureFlagConfig";

const extensionsByType = {
  [FileType.AUDIO]: ["mp3", "wav", "m4a", "flac", "aac", "wma", "ogg"],
  [FileType.SCREENER_SUMMARY]: ["csv", "xml"],
  [FileType.SCREENER_GUIDE]: ["pdf", "doc", "docx"],
  [FileType.DISCUSSION_GUIDE]: ["pdf", "doc", "docx"],
  [FileType.POWERPOINT]: ["ppt", "pptx"],
};

type Props = {
  label?: React.ReactNode;
  icon: OverridableComponent<SvgIconTypeMap & any>;

  study: Study;
  edit_context: EditStudyContext;
  fileEditState?: FileEditState;

  disabled?: boolean;
};

export default function EditDocumentFileUpload({
  label,
  icon,

  study,
  edit_context,
  fileEditState,

  disabled,
}: Props) {
  const PmrSwapSummaryFileFeature = useFeatureFlag(Feature.PmrSwapSummaryFile);
  const StudyFileStore = useStudyFileStore();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const setUploadProgress = (progress?: number) => edit_context.setUploadProgress(fileEditState!, progress);

  if (!fileEditState) {
    return <></>;
  }

  const { file_type, file, uploaded_file_guid, s3_file_location, original_name } = fileEditState;
  const hasFile = !!(file || s3_file_location);

  const makeFileName = (file: File, study: Study, file_type: FileType) => {
    const file_extension = file.name.split(".").pop();

    return edit_context.sanitizeString(
      [study.vendor.vendor_name, study.study_name, file_type, dayjs(study.study_start_date).format("YYYYMMDD")].join(
        "_",
      ) + `.${file_extension}`,
    );
  };

  let file_name: string | undefined;
  if (fileEditState.s3_file_location) {
    file_name = fileEditState.s3_file_location.split("/").at(-1);
  } else if (file) {
    file_name = makeFileName(file, study, file_type);
  }
  file_name = fileEditState.s3_file_location || hasFile ? file_name : undefined;

  const StyledIcon = ({ Icon }: { Icon: OverridableComponent<SvgIconTypeMap & any> }) => {
    return <Icon sx={{ width: "30px", height: "30px", color: "white" }} fontSize="medium" />;
  };

  const handleClickReplace = () => {
    // Trigger an update to remove the current file by its file_type
    edit_context.setGuideFile(file_type, undefined, undefined);
  };

  const handleClickBrowse = () => {
    inputRef.current?.click();
  };

  const uploadFileWithProgress = async (file: File, study: Study, file_type: FileType) => {
    // Start the spinner
    setUploadProgress(-1);

    const original_name = file.name;
    const file_name = makeFileName(file, study, file_type);

    const register = await StudyFileStore.registerFile(file_name, original_name);
    console.debug("File Registration UUID:", register.deepsense_file_guid);

    const s3_upload = await StudyFileStore.generateUploadUrl(register.deepsense_file_guid);
    console.debug("S3 Presigned URL:", s3_upload.presigned_url);

    // Switch to a positive value for determinate circular progress
    setUploadProgress(1);
    await StudyFileStore.uploadFile(s3_upload.presigned_url, file, (progressEvent: AxiosProgressEvent) => {
      const { loaded, total, rate } = progressEvent;

      if (loaded < (total ?? 1)) {
        const progress = (loaded * 100) / (total ?? 1);
        console.debug({ total, loaded, progress, rate });
        setUploadProgress(progress);
      } else {
        // Force last update frame to 100% since we don't always get a final frame
        setUploadProgress(100);

        setTimeout(() => {
          // Transition to a "working" animation
          setUploadProgress(-1);
          // Then then hide the spinner after 1 second
          setTimeout(() => setUploadProgress(undefined), 1000);
        }, 200);
      }
    });

    return register.deepsense_file_guid;
  };

  const handleFileSelect = async (file?: File) => {
    if (!file) {
      // Handle unselecting a file?
      edit_context.setGuideFile(file_type, undefined, undefined);
      return;
    }

    if (edit_context.uploadFileExists(file)) {
      emitSnackbar(
        `This file ${file?.name} has been uploaded more than once. Please review uploaded files and make the necessary changes.`,
        "info",
        { autoHideDuration: 10000 },
      );
      return;
    }

    // File extension check
    const extension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = extensionsByType[file_type];
    if (!allowedExtensions.includes(extension!)) {
      const allowedExtensionsText = allowedExtensions.map((ext: string) => `.${ext}`).join(", ");

      return emitSnackbar(`Only ${allowedExtensionsText} files are allowed`, "error", {
        autoHideDuration: 3000,
      });
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    // Start the Upload process
    const uploaded_file_guid = await uploadFileWithProgress(file, study, file_type).catch((reason) => {
      console.error("Filed to upload file", reason);
      // Unset the file on error
      edit_context.setGuideFile(file_type, undefined, undefined);
      setUploadProgress(undefined);

      emitSnackbar(`There was an error while uploading the selected file`, "warning", {
        persist: true,
      });
    });

    if (uploaded_file_guid) {
      edit_context.setGuideFile(file_type, file, uploaded_file_guid);

      // Special logic for when the user replaces the SCREENER_SUMMARY
      if (file_type == FileType.SCREENER_SUMMARY) {
        edit_context.processScreenerSummary(uploaded_file_guid);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles.length) return;

    const file = droppedFiles[0];
    const extension = (file?.name ?? "").toLowerCase().split(".").at(-1);

    const extensionsToCheck = extensionsByType[file_type];
    if (extensionsToCheck.includes(extension)) {
      return handleFileSelect(file);
    }

    // Else: Notify user of valid extensions
    const allowedExtensionsText = extensionsToCheck.map((ext: string) => `.${ext}`).join(", ");
    emitSnackbar(`Only ${allowedExtensionsText} files are allowed`, "error", {
      autoHideDuration: 10000,
    });
  };

  const handleDownload = () => {
    const file_name = (s3_file_location ?? "").split("/").at(-1);

    if (uploaded_file_guid && file_name) {
      StudyFileStore.downloadFile(uploaded_file_guid, file_name);
    }
  };

  const disableView = () => {
    const isDisabled = disabled;

    return {
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      pointerEvents: isDisabled ? "none" : "auto",
    };
  };

  return (
    <Box sx={{ ...disableView(), marginTop: "0.5em" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400" }}>
          {label}
        </Typography>
      </Box>
      <DragDropParent hasFile={!!file_name} isDragging={isDragging}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexBasis: file_name ? "0%" : "40%" }}>
          <Box
            sx={{
              width: "3rem",
              height: "3rem",
              background: file_name ? " #00A3E0" : " rgba(0, 163, 224, 0.3)",
              display: "flex",
              borderRadius: "5px",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: "0",
            }}
          >
            <StyledIcon Icon={icon} />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          {file_name ? (
            <Box sx={{ display: "flex", flex: 1, justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
                <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "400", color: "13294B" }}>
                  {file_name}
                </Typography>
                {(original_name || file?.name) && (
                  <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400", color: "13294B" }}>
                    Original file name: {original_name ?? file?.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex" }}>
                {file_type == FileType.SCREENER_SUMMARY && PmrSwapSummaryFileFeature.enabled && (
                  <IconButton color="primary" size="medium" onClick={handleClickReplace}>
                    <RepeatSharp />
                  </IconButton>
                )}
                <IconButton color="primary" size="medium" onClick={handleDownload}>
                  <FileDownloadOutlined />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <DragDrop
              acceptedFiles={extensionsByType[file_type].map((_) => `.${_}`).join(",")}
              inputRef={inputRef}
              onFileSelect={handleFileSelect}
              onClickBrowse={handleClickBrowse}
              setIsDragging={setIsDragging}
              handleDrop={handleDrop}
            />
          )}
        </Box>
        <FileProgressSpinner progress={fileEditState.uploadProgress} />
      </DragDropParent>
    </Box>
  );
}
